/**
 * Campaign Reports — create, list, and manage the passcode-protected client
 * report pages, at two levels:
 *
 *  - CAMPAIGN pages at culturegenesis.com/campaigns/{slug}/report — one GAM
 *    order each. The backend runs the three GAM reports (delivery / reach /
 *    device) detached, so a new page shows "Generating" for a few minutes
 *    before flipping to Ready; pages auto-refresh daily while the flight is
 *    live, finalize when data through the requested end date is complete, and
 *    EXPIRE 60 days after the flight ends.
 *  - ADVERTISER pages at culturegenesis.com/advertisers/{slug}/reports — one
 *    hub per GAM advertiser that lists every campaign page and opens all of
 *    them with ONE passcode. Creating one links existing campaign pages,
 *    creates the missing ones and generates their reports one at a time
 *    (detached); an hourly sync picks up newly approved orders.
 *
 * The picker groups GAM orders by advertiser (or lists them flat), the two
 * tables manage each level, and the modals live in ./CampaignReports.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, EmptyState, Spinner, Tabs } from '../ui';
import {
  CampaignReportPage,
  ReportableOrder,
  listCampaignReportPages,
  refreshCampaignReportPage,
  resetCampaignReportPagePin,
  restoreCampaignReportPage,
  revokeCampaignReportPage,
  updateCampaignReportPage,
} from '../../services/campaignReportsApi';
import {
  AdvertiserCandidateGroup,
  AdvertiserReportPage,
  getAdvertiserCandidates,
  listAdvertiserPages,
  resetAdvertiserPagePin,
  restoreAdvertiserPage,
  revokeAdvertiserPage,
  syncAdvertiserPage,
} from '../../services/advertiserReportsApi';
import type { ApiError } from '../../services/api';
import {
  MINUTES_PER_REPORT,
  Notify,
  fieldStyle,
  fmtDate,
  fmtRelative,
  isSyncLive,
  matchesQuery,
  plural,
  sectionSubtitleStyle,
  sectionTitleStyle,
  sortPickerRows,
} from './CampaignReports/shared';
import { useCopyFeedback } from './CampaignReports/useCopyFeedback';
import OrderPickerTable from './CampaignReports/OrderPickerTable';
import AdvertiserGroupCard from './CampaignReports/AdvertiserGroupCard';
import CreateAdvertiserPageModal from './CampaignReports/CreateAdvertiserPageModal';
import ManageAdvertiserPageModal from './CampaignReports/ManageAdvertiserPageModal';
import AdvertiserPagesTable from './CampaignReports/AdvertiserPagesTable';
import AdvertiserDeleteModal from './CampaignReports/AdvertiserDeleteModal';
import CampaignPagesTable from './CampaignReports/CampaignPagesTable';
import CampaignCreateModal from './CampaignReports/CampaignCreateModal';
import CampaignEditModal from './CampaignReports/CampaignEditModal';
import CampaignDeleteModal from './CampaignReports/CampaignDeleteModal';
import ResetPinModal from './CampaignReports/ResetPinModal';

interface CampaignReportsTabProps {
  notify: Notify;
}

/** Campaign table: poll while any page is generating so status pills flip without a reload. */
const CAMPAIGN_POLL_MS = 5000;
/** Advertiser table: poll while any hub sync is live (and the browser tab is visible). */
const HUB_POLL_MS = 20_000;

type PickerView = 'by-advertiser' | 'all';

type CampaignCreateTarget = { order: ReportableOrder; hub: { slug: string; label: string } | null };

const groupKey = (g: AdvertiserCandidateGroup) => g.advertiserId || `unknown:${g.advertiserName}`;

const successBanner: React.CSSProperties = {
  background: 'var(--success-soft)',
  border: '1px solid var(--success)',
  borderRadius: 'var(--radius-md)',
  padding: '12px 14px',
};

const bannerUrl: React.CSSProperties = {
  fontSize: 12,
  flex: 1,
  minWidth: 220,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const CampaignReportsTab: React.FC<CampaignReportsTabProps> = ({ notify }) => {
  const { copiedKey, copy } = useCopyFeedback(notify);

  // ---- Campaign pages -----------------------------------------------------
  const [pages, setPages] = useState<CampaignReportPage[]>([]);
  const [loadingPages, setLoadingPages] = useState(true);

  const refreshPages = useCallback(() => {
    setLoadingPages(true);
    listCampaignReportPages()
      .then(setPages)
      .catch((err) => notify('error', 'Failed to load campaign report pages', err?.message))
      .finally(() => setLoadingPages(false));
  }, [notify]);

  useEffect(() => {
    refreshPages();
  }, [refreshPages]);

  const anyGenerating = pages.some((p) => p.dataStatus === 'generating' && !p.revokedAt);
  useEffect(() => {
    if (!anyGenerating) return;
    const id = window.setInterval(() => {
      listCampaignReportPages().then(setPages).catch(() => {});
    }, CAMPAIGN_POLL_MS);
    return () => window.clearInterval(id);
  }, [anyGenerating]);

  // ---- Advertiser pages ---------------------------------------------------
  const [hubs, setHubs] = useState<AdvertiserReportPage[]>([]);
  const [loadingHubs, setLoadingHubs] = useState(true);

  const refreshHubs = useCallback(() => {
    listAdvertiserPages()
      .then(setHubs)
      .catch((err) => notify('error', 'Failed to load advertiser pages', err?.message))
      .finally(() => setLoadingHubs(false));
  }, [notify]);

  useEffect(() => {
    refreshHubs();
  }, [refreshHubs]);

  // ---- Picker (GAM orders grouped by advertiser) --------------------------
  const [groups, setGroups] = useState<AdvertiserCandidateGroup[]>([]);
  const [candidatesLoaded, setCandidatesLoaded] = useState(false);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [candidatesFetchedAt, setCandidatesFetchedAt] = useState<string | null>(null);

  const loadCandidates = useCallback(
    (force = false) => {
      setLoadingCandidates(true);
      getAdvertiserCandidates(force)
        .then((body) => {
          setGroups(body.advertisers);
          setCandidatesFetchedAt(body.fetchedAt);
          setCandidatesLoaded(true);
        })
        .catch((err) => notify('error', 'Failed to load GAM campaigns', err?.message))
        .finally(() => setLoadingCandidates(false));
    },
    [notify],
  );

  useEffect(() => {
    loadCandidates();
  }, [loadCandidates]);

  // Poll the hubs (and the campaign list, which gains the children a sync
  // creates) only while a sync is live AND the browser tab is visible.
  const anySyncing = hubs.some((h) => isSyncLive(h));
  useEffect(() => {
    if (!anySyncing) return;
    const id = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      listAdvertiserPages().then(setHubs).catch(() => {});
      listCampaignReportPages().then(setPages).catch(() => {});
    }, HUB_POLL_MS);
    return () => window.clearInterval(id);
  }, [anySyncing]);

  // When the last live sync finishes, the picker's page cross-references have
  // changed server-side (the sync invalidates its cache) — reload once.
  const wasSyncing = useRef(false);
  useEffect(() => {
    if (wasSyncing.current && !anySyncing) {
      loadCandidates();
      refreshPages();
    }
    wasSyncing.current = anySyncing;
  }, [anySyncing, loadCandidates, refreshPages]);

  const reloadAll = useCallback(() => {
    refreshPages();
    refreshHubs();
    loadCandidates();
  }, [refreshPages, refreshHubs, loadCandidates]);

  const [pickerView, setPickerView] = useState<PickerView>('by-advertiser');
  const [orderQuery, setOrderQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const q = orderQuery.trim().toLowerCase();
  const searching = q.length > 0;

  // One data source for both views: the grouped candidates. Search is the
  // same rule the server's `query` applies (campaign OR advertiser name).
  const filteredGroups = useMemo(
    () =>
      groups
        .map((group) => ({ group, orders: group.orders.filter((o) => matchesQuery(o, q)) }))
        .filter((x) => x.orders.length > 0),
    [groups, q],
  );
  const flatOrders = useMemo(
    () => sortPickerRows(groups.flatMap((g) => g.orders).filter((o) => matchesQuery(o, q))),
    [groups, q],
  );
  const totalOrders = useMemo(() => groups.reduce((n, g) => n + g.orders.length, 0), [groups]);
  const hubBySlug = useMemo(() => new Map(hubs.map((h) => [h.slug, h])), [hubs]);
  const allExpanded = groups.length > 0 && groups.every((g) => expanded.has(groupKey(g)));

  const toggleExpanded = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // ---- Modals & banners ---------------------------------------------------
  const [createTarget, setCreateTarget] = useState<CampaignCreateTarget | null>(null);
  const [createHubGroup, setCreateHubGroup] = useState<AdvertiserCandidateGroup | null>(null);
  const [manageSlug, setManageSlug] = useState<string | null>(null);
  const [manageRefreshKey, setManageRefreshKey] = useState(0);
  const [hubResetTarget, setHubResetTarget] = useState<AdvertiserReportPage | null>(null);
  const [hubDeleteTarget, setHubDeleteTarget] = useState<AdvertiserReportPage | null>(null);
  const [editTarget, setEditTarget] = useState<CampaignReportPage | null>(null);
  const [resetTarget, setResetTarget] = useState<CampaignReportPage | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CampaignReportPage | null>(null);
  const [minted, setMinted] = useState<{ url: string; pin: string; label: string } | null>(null);
  const [mintedHub, setMintedHub] = useState<{ url: string; pin: string; label: string; generateCount: number } | null>(null);

  const openCreateCampaign = (order: ReportableOrder) => {
    const hubSlug = order.existingAdvertiserPageSlug;
    const hub = hubSlug ? { slug: hubSlug, label: hubBySlug.get(hubSlug)?.clientLabel ?? order.advertiserName } : null;
    setCreateTarget({ order, hub });
  };

  const onCampaignCreated = (result: { url: string; pin: string; label: string }) => {
    setMinted(result);
    setCreateTarget(null);
    refreshPages();
    refreshHubs();
    loadCandidates();
    if (manageSlug) setManageRefreshKey((k) => k + 1); // the Manage roster now has this page
  };

  const onHubCreated = ({ page, url, generateCount }: { page: AdvertiserReportPage; url: string; generateCount: number }) => {
    setMintedHub({ url, pin: page.pin, label: page.clientLabel, generateCount });
    setCreateHubGroup(null);
    // Optimistic insert flagged live so polling starts before the detached sync writes its heartbeat.
    setHubs((prev) => [{ ...page, syncing: true }, ...prev.filter((h) => h.slug !== page.slug)]);
    refreshHubs();
    loadCandidates();
  };

  // ---- Advertiser page actions -------------------------------------------
  const runSync = async (slug: string, label: string) => {
    try {
      await syncAdvertiserPage(slug);
      setHubs((prev) =>
        prev.map((x) => (x.slug === slug ? { ...x, syncing: true, sync: { ...x.sync, status: 'running' } } : x)),
      );
      notify(
        'success',
        'Sync started',
        `Discovering ${label}’s campaigns now — new reports generate one at a time (~${MINUTES_PER_REPORT} min each).`,
      );
    } catch (e: unknown) {
      const err = e as ApiError;
      notify('error', err?.code === 'already_syncing' ? 'A sync is already running' : 'Sync failed to start', err?.message);
    }
  };

  const toggleRevokeHub = async (h: AdvertiserReportPage) => {
    try {
      if (h.revokedAt) {
        await restoreAdvertiserPage(h.slug);
        notify('success', 'Advertiser page restored');
      } else {
        if (
          !window.confirm(
            `Deactivate the advertiser page for “${h.clientLabel}”? Clients will see “deactivated” until you restore it; the campaign pages keep working with their own passcodes.`,
          )
        )
          return;
        await revokeAdvertiserPage(h.slug);
        notify('success', 'Advertiser page deactivated');
      }
      refreshHubs();
      loadCandidates();
    } catch (err: any) {
      notify('error', 'Action failed', err?.message);
    }
  };

  const resetHubPin = async (pin: string | undefined) => {
    if (!hubResetTarget) return;
    try {
      const page = await resetAdvertiserPagePin(hubResetTarget.slug, pin);
      notify('success', `New PIN for ${page.clientLabel}: ${page.pin}`, 'It also opens every campaign page listed on the advertiser page.');
      setHubResetTarget(null);
      refreshHubs();
    } catch (err: any) {
      notify('error', 'PIN reset failed', err?.message);
    }
  };

  // ---- Campaign page actions ---------------------------------------------
  const runRefreshData = async (p: CampaignReportPage) => {
    try {
      await refreshCampaignReportPage(p.slug);
      notify('success', 'Refresh started', 'GAM report jobs run for a few minutes — the status flips to Ready when done.');
      setPages((prev) => prev.map((x) => (x.slug === p.slug ? { ...x, dataStatus: 'generating' } : x)));
    } catch (err: any) {
      notify('error', 'Refresh failed to start', err?.message);
    }
  };

  const extendExpiry = async (p: CampaignReportPage) => {
    try {
      const base = Math.max(Date.parse(p.expiresAt) || Date.now(), Date.now());
      const page = await updateCampaignReportPage(p.slug, {
        expiresAt: new Date(base + 30 * 86_400_000).toISOString(),
      });
      notify('success', `Expiry extended to ${fmtDate(page.expiresAt)}`);
      refreshPages();
    } catch (err: any) {
      notify('error', 'Failed to extend expiry', err?.message);
    }
  };

  const resetCampaignPin = async (pin: string | undefined) => {
    if (!resetTarget) return;
    try {
      const page = await resetCampaignReportPagePin(resetTarget.slug, pin);
      notify('success', `New PIN for ${page.clientLabel}: ${page.pin}`);
      setResetTarget(null);
      refreshPages();
    } catch (err: any) {
      notify('error', 'PIN reset failed', err?.message);
    }
  };

  const toggleRevoke = async (p: CampaignReportPage) => {
    try {
      if (p.revokedAt) {
        await restoreCampaignReportPage(p.slug);
        notify('success', 'Page restored');
      } else {
        if (!window.confirm(`Deactivate the page for “${p.clientLabel}”? Clients will see “deactivated” until you restore it.`)) return;
        await revokeCampaignReportPage(p.slug);
        notify('success', 'Page deactivated');
      }
      refreshPages();
      refreshHubs();
      loadCandidates();
    } catch (err: any) {
      notify('error', 'Action failed', err?.message);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {mintedHub && (
        <div style={successBanner}>
          <div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 6 }}>
            ✅ Advertiser page created for {mintedHub.label} — share the link and passcode with the client.
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <code style={bannerUrl}>{mintedHub.url}</code>
            <code style={{ fontSize: 13, fontWeight: 700 }}>PIN {mintedHub.pin}</code>
            <Button size="sm" variant="primary" onClick={() => copy('mintedHub', `${mintedHub.url}\nPasscode: ${mintedHub.pin}`)}>
              {copiedKey === 'mintedHub' ? 'Copied ✓' : 'Copy link + PIN'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setMintedHub(null)}>Dismiss</Button>
          </div>
          <div style={{ fontSize: 12, color: 'var(--fg-3)', marginTop: 6 }}>
            {mintedHub.generateCount > 0
              ? `${plural(mintedHub.generateCount, 'campaign page')} ${mintedHub.generateCount === 1 ? 'is' : 'are'} being created and generate one at a time (~${MINUTES_PER_REPORT} min each) — progress below.`
              : 'Existing campaign pages are being linked — progress below.'}{' '}
            New campaigns, delivery data and device breakdowns refresh hourly. Reach refreshes daily (Google’s estimate, up to 3 days behind), or when you click Sync now.
          </div>
        </div>
      )}

      {minted && (
        <div style={successBanner}>
          <div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 6 }}>
            ✅ Report page created for {minted.label} — share the link and passcode with the client.
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <code style={bannerUrl}>{minted.url}</code>
            <code style={{ fontSize: 13, fontWeight: 700 }}>PIN {minted.pin}</code>
            <Button size="sm" variant="primary" onClick={() => copy('minted', `${minted.url}\nPasscode: ${minted.pin}`)}>
              {copiedKey === 'minted' ? 'Copied ✓' : 'Copy link + PIN'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setMinted(null)}>Dismiss</Button>
          </div>
          <div style={{ fontSize: 12, color: 'var(--fg-3)', marginTop: 6 }}>
            The report data is generating from GAM now — the page shows “preparing” for a few minutes, then loads automatically.
          </div>
        </div>
      )}

      {/* ---- Campaign picker ---- */}
      <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={sectionTitleStyle}>Create report pages</div>
            <div style={sectionSubtitleStyle}>
              GAM campaigns delivering, approved, paused, in work, or ended in the last 30 days — grouped by advertiser. One
              advertiser page gives a client a single passcode for every campaign report.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {candidatesFetchedAt && (
              <span style={{ fontSize: 11.5, color: 'var(--fg-4)' }} title={new Date(candidatesFetchedAt).toLocaleString()}>
                GAM sweep {fmtRelative(candidatesFetchedAt)}
              </span>
            )}
            <Button size="sm" variant="ghost" onClick={() => loadCandidates(true)} disabled={loadingCandidates}>
              {loadingCandidates ? 'Refreshing…' : '↻ Refresh from GAM'}
            </Button>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Tabs<PickerView>
            size="sm"
            variant="segmented"
            value={pickerView}
            onChange={setPickerView}
            ariaLabel="Picker view"
            options={[
              { value: 'by-advertiser', label: 'By advertiser', count: groups.length },
              { value: 'all', label: 'All campaigns', count: totalOrders },
            ]}
          />
          <input
            style={{ ...fieldStyle, width: 'auto', flex: '1 1 260px', maxWidth: 420 }}
            value={orderQuery}
            onChange={(e) => setOrderQuery(e.target.value)}
            placeholder="Search campaigns by name or advertiser…"
            aria-label="Search campaigns"
          />
          {pickerView === 'by-advertiser' && groups.length > 0 && !searching && (
            <Button size="sm" variant="ghost" onClick={() => setExpanded(allExpanded ? new Set() : new Set(groups.map(groupKey)))}>
              {allExpanded ? 'Collapse all' : 'Expand all'}
            </Button>
          )}
        </div>

        {loadingCandidates && !candidatesLoaded ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '26px 0', justifyContent: 'center', color: 'var(--fg-3)', fontSize: 13 }}>
            <Spinner /> Sweeping GAM for campaigns — the first load takes a few seconds…
          </div>
        ) : groups.length === 0 ? (
          <EmptyState
            title="No reportable campaigns"
            subtitle="Nothing is delivering, approved, paused, in work, or recently ended in GAM."
          />
        ) : pickerView === 'all' ? (
          flatOrders.length === 0 ? (
            <EmptyState title="No campaigns match" subtitle="Try a different campaign or advertiser name." />
          ) : (
            <OrderPickerTable orders={flatOrders} onCreate={openCreateCampaign} />
          )
        ) : filteredGroups.length === 0 ? (
          <EmptyState title="No campaigns match" subtitle="Try a different campaign or advertiser name." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filteredGroups.map(({ group, orders }) => {
              const key = groupKey(group);
              const hubSlug = group.existingAdvertiserPageSlug;
              const hub = hubSlug ? hubBySlug.get(hubSlug) ?? null : null;
              return (
                <AdvertiserGroupCard
                  key={key}
                  group={group}
                  orders={orders}
                  expanded={searching || expanded.has(key)}
                  onToggle={() => toggleExpanded(key)}
                  hub={hub}
                  copied={copiedKey === `hublink:${hubSlug}`}
                  onCreateAdvertiserPage={() => setCreateHubGroup(group)}
                  onCopyHub={() => hub && copy(`hublink:${hub.slug}`, `${hub.url}\nPasscode: ${hub.pin}`)}
                  onSyncHub={() => hubSlug && runSync(hubSlug, hub?.clientLabel ?? group.advertiserName)}
                  onManageHub={() => hubSlug && setManageSlug(hubSlug)}
                  onCreateCampaign={openCreateCampaign}
                />
              );
            })}
          </div>
        )}
      </section>

      {/* ---- Advertiser pages ---- */}
      <AdvertiserPagesTable
        pages={hubs}
        loading={loadingHubs}
        copiedKey={copiedKey}
        onCopy={copy}
        onSync={(h) => runSync(h.slug, h.clientLabel)}
        onManage={(h) => setManageSlug(h.slug)}
        onResetPin={setHubResetTarget}
        onToggleRevoke={toggleRevokeHub}
        onDelete={setHubDeleteTarget}
      />

      {/* ---- Campaign pages ---- */}
      <CampaignPagesTable
        pages={pages}
        loading={loadingPages}
        hubs={hubs}
        copiedKey={copiedKey}
        onCopy={copy}
        onRefreshData={runRefreshData}
        onExtendExpiry={extendExpiry}
        onEdit={setEditTarget}
        onResetPin={setResetTarget}
        onToggleRevoke={toggleRevoke}
        onDelete={setDeleteTarget}
      />

      {/* ---- Modals (later ones stack on top) ---- */}
      {createHubGroup && (
        <CreateAdvertiserPageModal
          key={groupKey(createHubGroup)}
          group={createHubGroup}
          groups={groups}
          onClose={() => setCreateHubGroup(null)}
          onCreated={onHubCreated}
          notify={notify}
        />
      )}
      {manageSlug && (
        <ManageAdvertiserPageModal
          key={manageSlug}
          slug={manageSlug}
          groups={groups}
          refreshKey={manageRefreshKey}
          onClose={() => setManageSlug(null)}
          onSaved={reloadAll}
          onCreateCampaign={(order, hub) => setCreateTarget({ order, hub })}
          notify={notify}
        />
      )}
      {createTarget && (
        <CampaignCreateModal
          key={createTarget.order.gamOrderId}
          order={createTarget.order}
          hub={createTarget.hub}
          onClose={() => setCreateTarget(null)}
          onCreated={onCampaignCreated}
          notify={notify}
        />
      )}
      {editTarget && (
        <CampaignEditModal
          key={editTarget.slug}
          page={editTarget}
          hubs={hubs}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            refreshPages();
            refreshHubs();
          }}
          notify={notify}
        />
      )}
      {resetTarget && (
        <ResetPinModal
          eyebrow="Campaign reports"
          label={resetTarget.clientLabel}
          currentPin={resetTarget.pin}
          onClose={() => setResetTarget(null)}
          onSubmit={resetCampaignPin}
        />
      )}
      {hubResetTarget && (
        <ResetPinModal
          eyebrow="Advertiser pages"
          label={hubResetTarget.clientLabel}
          currentPin={hubResetTarget.pin}
          scopeNote="The new passcode also opens every campaign page listed on this advertiser page."
          onClose={() => setHubResetTarget(null)}
          onSubmit={resetHubPin}
        />
      )}
      {deleteTarget && (
        <CampaignDeleteModal
          page={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={reloadAll}
          notify={notify}
        />
      )}
      {hubDeleteTarget && (
        <AdvertiserDeleteModal
          page={hubDeleteTarget}
          onClose={() => setHubDeleteTarget(null)}
          onDeleted={reloadAll}
          notify={notify}
        />
      )}
    </div>
  );
};

export default CampaignReportsTab;
